import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { postSignup } from "../erpClient";
import toast from "react-hot-toast";
import AuthLayout from "../components/AuthLayout";
import "../components/auth-fields.css";
import "../components/AuthLayout.css";
import {
  isValidMobileNumber,
  mobileInputFieldProps,
  sanitizeMobileInput,
} from "../utils/mobileInput";
import { isValidEmail } from "../utils/emailInput";

const SignUp = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatPhoneForDisplay = (digits) => {
    if (!digits) return "";
    return digits.split("").join(" ");
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    let field = id;
    if (id === "text-name") field = "name";
    if (id === "mobile-number") field = "phone";

    let nextValue = value;
    if (field === "phone") {
      nextValue = sanitizeMobileInput(value);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: nextValue,
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!isValidMobileNumber(formData.phone)) {
      newErrors.phone = "Please enter a valid 10-digit mobile number";
    }

    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = "Enter a valid email address (must include @)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const registrationData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
      };

      const data = await postSignup(registrationData);

      if (data && data.success) {
        toast.success("Registration successful");
        navigate("/login");
      } else {
        toast.error(data?.message || "Registration failed");
      }
    } catch (error) {
      console.error("Signup error:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Registration failed. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="auth-form__head">
        <h1 className="auth-form__head-title">Create your account</h1>
        <p className="auth-form__head-sub">
          Fill in your details to register for the platform
        </p>
      </div>

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="auth-form__field">
          <label htmlFor="text-name" className="form-label">
            Full name
          </label>
          <div
            className={`auth-field__control ${errors.name ? "is-invalid" : ""}`}
          >
            <input
              type="text"
              className="auth-field__input auth-field__input--full"
              id="text-name"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleChange}
              autoComplete="name"
            />
          </div>
          {errors.name && (
            <div className="invalid-feedback d-block">{errors.name}</div>
          )}
        </div>

        <div className="auth-form__field">
          <label htmlFor="mobile-number" className="form-label">
            Mobile number
          </label>
          <div
            className={`auth-field__control ${errors.phone ? "is-invalid" : ""}`}
          >
            <span className="auth-field__prefix">+91</span>
            <input
              {...mobileInputFieldProps}
              className="auth-field__input"
              id="mobile-number"
              placeholder="1234567890"
              // Spaced display ("1 2 3 …") needs room past raw 10 digits (Login uses 19).
              maxLength={19}
              value={formatPhoneForDisplay(formData.phone)}
              onChange={handleChange}
            />
          </div>
          {errors.phone && (
            <div className="invalid-feedback d-block">{errors.phone}</div>
          )}
        </div>

        <div className="auth-form__field">
          <label htmlFor="email" className="form-label">
            Email
          </label>
          <div
            className={`auth-field__control ${errors.email ? "is-invalid" : ""}`}
          >
            <input
              type="email"
              className="auth-field__input auth-field__input--full"
              id="email"
              placeholder="you@company.com"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
            />
          </div>
          {errors.email && (
            <div className="invalid-feedback d-block">{errors.email}</div>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-primary auth-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating account…" : "Sign up"}
        </button>

        <p className="auth-form__footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default SignUp;
